import { useMemo } from "react";
import {
  Col,
  DatePicker,
  Form,
  FormItemProps,
  Input,
  Select,
  SelectProps,
} from "antd";
import { TextArea, RadioGroup, RadioButton } from "@libertify/ui";
import { LanguageSelect } from "components/LanguageSelect/";
import { ToggleSelect } from "./ToggleSelect";
import { LabeledToggle } from "./LabeledToggle";
import styled from "styled-components";

export type FormItemColProps = {
  name: string;
  label?: string;
  prefix?: string | React.ReactNode;
  helpText?: string;
  component:
    | "input"
    | "password"
    | "textarea"
    | "select"
    | "language"
    | "date"
    | "toggleSelect"
    | "radio"
    | "toggle";
  rows?: number;
  // Translation key for the toggle's label (component: "toggle").
  toggleLabel?: string;
  options?: SelectProps["options"];
  optionGroups?: SelectProps["options"];
  placeholder?: string;
  mode?: SelectProps["mode"];
  optionRender?: (option: {
    label?: React.ReactNode;
    value?: string | number;
  }) => React.ReactNode;
  colSpan?: { xs: number; md?: number };
  allowClear?: boolean;
  rules?: FormItemProps["rules"];
  tooltip?: string;
};

export const FormItemCol = ({
  name,
  label,
  prefix,
  helpText,
  component,
  rows,
  toggleLabel,
  placeholder,
  mode,
  optionRender,
  colSpan,
  options,
  optionGroups,
  allowClear,
  rules,
  tooltip,
}: FormItemColProps) => {
  // Radios show their hint below the options; other fields keep the tooltip.
  const isRadio = component === "radio";
  const isToggle = component === "toggle";

  const formComponent = useMemo(() => {
    switch (component) {
      case "input":
        return <Input placeholder={placeholder} prefix={prefix} />;
      case "password":
        // Mirrors the Share modal: masked input, no browser autofill.
        return (
          <Input.Password
            placeholder={placeholder}
            prefix={prefix}
            autoComplete="new-password"
          />
        );
      case "textarea":
        return <TextArea placeholder={placeholder} rows={rows ?? 2} />;
      case "language":
        return (
          <LanguageSelect
            style={{ width: "100%" }}
            placeholder={placeholder}
            allowClear
            includeAuto
          />
        );
      case "select":
        return (
          <Select
            options={optionGroups || options}
            placeholder={placeholder}
            allowClear={allowClear ?? true}
            mode={mode}
            optionRender={optionRender}
          />
        );
      case "toggleSelect":
        return (
          <ToggleSelect
            options={options}
            placeholder={placeholder}
            toggleLabel={toggleLabel}
          />
        );

      case "toggle":
        return <LabeledToggle toggleLabel={toggleLabel} />;
      case "radio":
        // Must be the single child so Form.Item can inject value/onChange.
        return (
          <RadioGroup>
            {(options ?? []).map((option) => (
              <RadioButton key={String(option.value)} value={option.value}>
                {option.label}
              </RadioButton>
            ))}
          </RadioGroup>
        );
      case "date":
        return (
          <DatePicker
            placeholder={placeholder}
            format="YYYY-MM-DD"
            style={{ width: "100%" }}
          />
        );
      default:
        return null;
    }
  }, [
    component,
    prefix,
    placeholder,
    mode,
    optionRender,
    allowClear,
    rows,
    toggleLabel,
    options,
    optionGroups,
  ]);

  return (
    <Col key={name} xs={24} {...colSpan}>
      <Form.Item
        label={label}
        name={name}
        rules={rules}
        valuePropName={isToggle ? "checked" : undefined}
        tooltip={isRadio || isToggle ? undefined : (tooltip ?? helpText)}
        extra={
          (isRadio || isToggle) && helpText ? (
            <StyledHint isToggle={isToggle}>{helpText}</StyledHint>
          ) : undefined
        }
      >
        {formComponent}
      </Form.Item>
    </Col>
  );
};

const StyledHint = styled.span<{ isToggle: boolean }>`
  font-style: italic;
  display: block;
  margin-top: ${({ isToggle }) => (isToggle ? 0 : 10)}px;
  font-size: 12px;
  color: ${({ theme }) => theme.colorTextDescription};
`;
